import { z } from "zod"
import { marketplaceIdSchema } from "./marketplace-id"
import {
  orderCostVerificationSchema,
  PNL_ORDER_COST_POLICY,
  PNL_SOURCE_BASIS,
} from "./order-cost-evidence"

export const PNL_COST_POLICY = "marketplace-created-at-v1"
export const PNL_COST_CLASS = {
  proven: "proven",
  estimated: "estimated",
  unknown: "unknown",
  legacy: "legacy",
} as const
export const PNL_COST_ACTION = { activate: "activate", correct: "correct" } as const
export const pnlCostCountSchema = z.object({
  lines: z.number().nonnegative(),
  units: z.number().nonnegative(),
})
export const pnlCostCoverageSchema = z.object({
  proven: pnlCostCountSchema,
  estimated: pnlCostCountSchema,
  unknown: pnlCostCountSchema,
  legacy: z.object({ lines: z.number().nonnegative().nullable(), units: z.number().nonnegative() }),
  // Cost lines include source-only records; units above remain marketplace sale units.
  sourceOnly: z
    .object({
      lines: z.number().nonnegative(),
      units: z.number().nonnegative(),
      unknownLines: z.number().nonnegative(),
    })
    .optional(),
  coveredCogs: z.number().nullable(),
  exceptions: z
    .array(z.object({ reason: z.string(), lines: z.number().int().nonnegative() }))
    .optional(),
  missingDays: z.number().int().nonnegative(),
  qualification: z.enum(PNL_COST_CLASS),
})
export type PnlCostCoverage = z.infer<typeof pnlCostCoverageSchema>
export const pnlCostSelectionSchema = z.object({
  classification: z.enum(["proven", "estimated", "unknown"]),
  reason: z.string(),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  taxBasis: z.string().nullable(),
  historyId: z.number().int().nullable(),
  simpleSku: z.string().nullable(),
  store: z.string(),
  originalIdentifier: z.string(),
  resolution: z.string(),
  saleAt: z.iso.datetime(),
  policy: z.enum([PNL_COST_POLICY, PNL_ORDER_COST_POLICY]),
  evidence: z.record(z.string(), z.unknown()),
})
export type PnlCostSelection = z.infer<typeof pnlCostSelectionSchema>
export const costSourceVerificationSchema = z
  .object({
    // Existing immutable product verification records predate the discriminant.
    basis: z.literal(PNL_SOURCE_BASIS.product).optional(),
    store: z.string().min(1),
    source: z.string().min(1),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    taxBasis: z.literal("net"),
    reference: z.string().min(10).max(2000),
    retentionAssessment: z.string().min(10).max(4000),
    writePathAssessment: z.string().min(10).max(4000),
    effectiveTimeEvidence: z
      .object({
        historyIds: z.array(z.number().int().positive()).min(1).max(100),
        reference: z.string().min(10).max(2000),
      })
      .optional(),
    captureStartedAt: z.iso.datetime().optional(),
  })
  .refine((value) => value.from <= value.to, "Verification range must be ordered")
export type CostSourceVerification = z.infer<typeof costSourceVerificationSchema>
export const pnlSourceVerificationSchema = z.union([
  orderCostVerificationSchema,
  costSourceVerificationSchema,
])
export type PnlSourceVerification = z.infer<typeof pnlSourceVerificationSchema>
export const pnlCostPreviewBodySchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal(PNL_COST_ACTION.activate),
    months: z
      .array(z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/))
      .min(1)
      .max(12),
    verification: pnlSourceVerificationSchema,
    reason: z.string().min(1).max(2000),
    compatibleWritersDrained: z.literal(true),
  }),
  z.object({
    action: z.literal(PNL_COST_ACTION.correct),
    selectionIds: z.array(z.number().int().positive()).min(1).max(100),
    reason: z.string().min(1).max(2000),
    evidence: z.string().min(10).max(4000),
    verification: pnlSourceVerificationSchema.optional(),
  }),
])
export type PnlCostPreviewBody = z.infer<typeof pnlCostPreviewBodySchema>
export const pnlCostConfirmBodySchema = z.object({
  previewId: z.string().uuid(),
  fingerprint: z.string().length(64),
  confirm: z.literal(true),
})
export const pnlCostPauseBodySchema = z.object({ reason: z.string().min(1).max(2000) })
export const pnlCostEvidenceQuerySchema = z.object({
  after: z.coerce.number().int().nonnegative().default(0),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  orderId: z.string().max(200).optional(),
})

export const pnlBreakdownSchema = z.object({
  netRevenue: z.number(),
  commission: z.number(),
  shippingCost: z.number(),
  fixedFees: z.number(),
  feesTotal: z.number(),
  cogs: z.number().nullable(),
  spend: z.number(),
  netProfit: z.number().nullable(),
  marginPct: z.number().nullable(),
  acosPct: z.number().nullable(),
  unitsSold: z.number(),
  ordersCount: z.number(),
  aov: z.number().nullable(),
  ppcRevenue: z.number(),
  ppcUnits: z.number(),
  ppcAcosPct: z.number().nullable(),
  organicRevenue: z.number(),
  organicUnits: z.number(),
  ppcSharePct: z.number().nullable(),
  costCoverage: pnlCostCoverageSchema,
  legacy: z
    .object({ cogs: z.number(), netProfit: z.number(), marginPct: z.number().nullable() })
    .nullable(),
})
export type PnlBreakdown = z.infer<typeof pnlBreakdownSchema>
export const marketplacePnlResponseSchema = z.object({
  costControl: z.object({
    state: z.string(),
    months: z.array(z.string()),
    sourceBasis: z.enum(PNL_SOURCE_BASIS).nullable(),
    exceptions: z.array(z.object({ reason: z.string(), lines: z.number(), action: z.string() })),
    latestWork: z
      .object({ id: z.string(), kind: z.string(), state: z.string(), error: z.string().nullable() })
      .nullable(),
  }),
  marketplace: marketplaceIdSchema,
  months: z.number(),
  currency: z.string(),
  from: z.string(),
  to: z.string(),
  totals: pnlBreakdownSchema,
  byMonth: z.array(pnlBreakdownSchema.extend({ month: z.string() })),
})
